import { FC } from "react";

import Stack from "@mui/material/Stack";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardDivider } from "@/dashboard-design-system/Divider";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { ChevronLeft, Cross } from "@/icons/outlined";

export interface HeaderProps {
  title: string;
  onClose: () => void;
  onBack?: () => void;
  isMobile: boolean;
}

export const Header: FC<HeaderProps> = ({ title, onClose, onBack, isMobile }) => {
  const { t } = useTranslate();

  const closeButton = (
    <DashboardIconButton
      variant="text"
      vibe="neutral"
      size={isMobile ? "medium" : "small"}
      onClick={onClose}
      aria-label={t("dashboard", "common.close")}
      data-test="check-in-tools-drawer-close-button"
    >
      <Cross />
    </DashboardIconButton>
  );

  const backButton = onBack && (
    <DashboardIconButton
      variant="text"
      vibe="neutral"
      size={isMobile ? "medium" : "small"}
      onClick={onBack}
      aria-label={t("dashboard", "checkInToolsDrawer.manageCollaborators.back")}
      data-test="check-in-tools-drawer-back-button"
    >
      <ChevronLeft />
    </DashboardIconButton>
  );

  if (isMobile) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{ paddingTop: 3, paddingLeft: backButton ? 2 : 3, paddingRight: 2, paddingBottom: 1.5 }}
      >
        {backButton}
        <DashboardTypography variant="h5" vibe="text-neutral-intense" sx={{ flex: 1 }}>
          {title}
        </DashboardTypography>
        {closeButton}
      </Stack>
    );
  }

  return (
    <Stack gap={3} sx={{ paddingTop: 3, paddingX: 3 }}>
      <Stack direction="row" alignItems="center" gap={2}>
        {backButton ?? closeButton}
        <DashboardTypography variant="h6" vibe="text-neutral-intense">
          {title}
        </DashboardTypography>
      </Stack>
      <DashboardDivider />
    </Stack>
  );
};
